import { Button } from '@mui/material';
import { awaitAllSafely, isEmptyObject } from '@vy/utils';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntegrationContextProvider } from '../../../../hooks/use-integration-context-provider';
import { useConnectToPaddle } from '../../hooks/connect-to-paddle';
import { useFetchPaddleSettings } from '../hooks/useFetchPaddleSettings';
import { useModifyConfiguration } from '../hooks/useModifyConfiguration';
import type {
  PaddleConfigurationControl,
  PaddleConfigurationFormValues,
} from '../PaddleConfigurationFormSchema';
import { getPaddleIntegrationSettings } from './utils';

type PaddleConfigurationFooterProps = {
  control: PaddleConfigurationControl;
};

export const PaddleConfigurationFooter: FC<PaddleConfigurationFooterProps> = ({ control }) => {
  const { integrationId, configuration: existingConfiguration } = useFetchPaddleSettings();
  const updatePaddleConfiguration = useModifyConfiguration();
  const reconnectToPaddle = useConnectToPaddle(control);
  const { clearSelectedIntegration } = useIntegrationContextProvider();

  const {
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useFormContext<PaddleConfigurationFormValues>();

  const { apiToken: isApiTokenDirty, ...formDirtyValues } = dirtyFields;

  const getReconnectToPaddlePromise = async (formValues: PaddleConfigurationFormValues) => {
    if (!isApiTokenDirty) {
      return undefined;
    }

    return reconnectToPaddle(formValues.apiToken, formValues.environment);
  };

  const getUpdatePaddleConfigurationPromise = async (formValues: PaddleConfigurationFormValues) => {
    if (isEmptyObject(formDirtyValues)) {
      return undefined;
    }

    const existingIntegrationSettings = getPaddleIntegrationSettings(existingConfiguration);
    const updatedIntegrationSettings = getPaddleIntegrationSettings(formValues);

    return updatePaddleConfiguration(integrationId, updatedIntegrationSettings, existingIntegrationSettings);
  };

  const submitHandler = handleSubmit(async (formValues) => {
    const reconnectToPaddlePromise = getReconnectToPaddlePromise(formValues);
    const updatePaddleConfigurationPromise = getUpdatePaddleConfigurationPromise(formValues);

    await awaitAllSafely(reconnectToPaddlePromise, updatePaddleConfigurationPromise);

    reset(formValues);
    clearSelectedIntegration();
  });

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button variant="contained" onClick={submitHandler}>
        Save Configuration
      </Button>
    </div>
  );
};
